import { CreditCard } from 'lucide-react'
import { useState } from 'react'
import { FloatingField } from '#app/components/application-detail/dialogs/dialog-bits'
import { Button } from '#app/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog'

/**
 * Renew Credits modal (TT-BILL-108) — written once, reused by the landlord
 * billing settings page and the application-detail page (TT-BILL-109). Posts to
 * /resources/billing/topup and redirects to the Stripe-hosted Checkout URL.
 * No useEffect: the submit handler awaits the response and redirects imperatively.
 */

type Pack = { label: string; amount: number | 'custom' }

const PACKS: Pack[] = [
	{ label: 'Starter', amount: 35 },
	{ label: 'Popular', amount: 100 },
	{ label: 'Pro', amount: 'custom' },
]

export function RenewCreditsDialog({
	open,
	onOpenChange,
	currentBalance,
	ratio,
	minDollars,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	currentBalance: number
	ratio: number
	minDollars: number
}) {
	const [selected, setSelected] = useState<Pack>(PACKS[0])
	const [customAmount, setCustomAmount] = useState('')
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [idemKey] = useState(() =>
		typeof crypto !== 'undefined' && 'randomUUID' in crypto
			? crypto.randomUUID()
			: String(Date.now()),
	)

	const amountDollars =
		selected.amount === 'custom' ? Number(customAmount) || 0 : selected.amount
	const credits =
		ratio > 0 ? Math.round((amountDollars / ratio) * 100) / 100 : 0
	const belowMin = amountDollars < minDollars

	async function handlePay() {
		setError(null)
		if (belowMin || amountDollars <= 0) {
			setError(`Amount should not be less than $${minDollars}.`)
			return
		}
		setSubmitting(true)
		try {
			const body = new FormData()
			body.set('amount', String(amountDollars))
			// Scope the Stripe idempotency key to the amount (TT2-521): the key is
			// generated once per dialog open, so reusing it across Pay attempts with
			// a different amount — e.g. switching to a custom amount after trying a
			// pack — made Stripe reject the call ("same key, different parameters")
			// and surfaced as a generic checkout error. Same amount still dedupes
			// double-clicks; a new amount gets a fresh key.
			body.set(
				'idempotencyKey',
				`${idemKey}:${Math.round(amountDollars * 100)}`,
			)
			const res = await fetch('/resources/billing/topup', {
				method: 'POST',
				body,
			})
			const json = (await res.json()) as { url?: string; error?: string }
			if (json.url) {
				window.location.href = json.url
				return
			}
			setError(json.error ?? 'Could not start checkout. Please try again.')
		} catch {
			setError('Could not start checkout. Please try again.')
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-[28px] bg-[#fbfdfd] p-0 [scrollbar-width:thin]">
				<DialogHeader className="px-6 pt-6 pb-2">
					<DialogTitle className="text-2xl">Renew Credit</DialogTitle>
					<p className="text-[#32302F]">
						<span className="text-[20px] font-semibold">{currentBalance}</span>{' '}
						<span className="text-[14px]">Credits Remaining</span>
					</p>
				</DialogHeader>

				<div className="px-6 py-4 -mt-[20px]">
					<div className="rounded-2xl p-4 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
						<h3 className="text-sm font-medium	 text-[#32302F]">
							Choose A Credit Pack
						</h3>
						<p className="mt-2 flex items-center gap-2 rounded-md bg-[#F2F7FC] px-3 py-1.5 text-xs text-[#01389D]">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="24"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								className="lucide lucide-circle-alert-icon lucide-circle-alert size-4"
							>
								<circle cx="12" cy="12" r="10" />
								<line x1="12" x2="12" y1="8" y2="12" />
								<line x1="12" x2="12.01" y1="16" y2="16" />
							</svg>
							Amount Is Equivalent To Credits
						</p>

						<div className="mt-3 grid grid-cols-3 gap-3">
							{PACKS.map((pack) => {
								const isSel = pack.label === selected.label
								return (
									<button
										key={pack.label}
										type="button"
										onClick={() => setSelected(pack)}
										className={`relative rounded-xl border p-3 text-left transition ${
											isSel
												? 'border-[#32302F] ring-0.5 ring-[#32302F]'
												: 'border-[#F0F0F0]'
										}`}
									>
										<span
											className={`absolute top-3 right-3 flex h-4 w-4 items-center justify-center rounded-full border ${
												isSel ? 'border-[#32302F]' : 'border-[#D4D4D4]'
											}`}
										>
											{isSel ? (
												<span className="h-2 w-2 rounded-full bg-[#32302F]" />
											) : null}
										</span>
										<div className="text-sm text-[#747474]">{pack.label}</div>
										<div className="text-2xl font-semibold text-[#32302F]">
											{pack.amount === 'custom' ? 'Custom' : `$ ${pack.amount}`}
										</div>
									</button>
								)
							})}
						</div>

						{selected.amount === 'custom' ? (
							<FloatingField
								label="Enter Amount"
								htmlFor="custom-amount"
								className="mt-4"
							>
								<input
									id="custom-amount"
									type="number"
									min={minDollars}
									step="any"
									inputMode="decimal"
									value={customAmount}
									onChange={(e) => setCustomAmount(e.target.value)}
									placeholder={`Amount Should Not Be Less Than $${minDollars}`}
									className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
								/>
							</FloatingField>
						) : null}
					</div>

					<div className="mt-4 rounded-2xl p-4 shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]">
						<div className="flex items-center justify-between">
							<span className="text-sm font-semibold text-[#32302F]">
								{credits} Credits
							</span>
							<span className="text-sm text-[#32302F]">
								${amountDollars.toFixed(2)}
							</span>
						</div>
						<div className="mt-1 flex items-center justify-between text-sm text-[#747474]">
							<span>Tax</span>
							<span>Calculated at checkout</span>
						</div>
						<div className="mt-3 flex items-center justify-between border-t border-[#F3F3F3] pt-3">
							<span className="text-sm font-semibold text-[#32302F]">
								Total
							</span>
							<span className="text-sm font-semibold text-[#32302F]">
								${amountDollars.toFixed(2)} + tax
							</span>
						</div>
					</div>

					{error ? (
						<p className="mt-3 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700">
							{error}
						</p>
					) : null}
				</div>

				<div className="flex items-center justify-between border-t border-[#F3F3F3] px-6 py-4">
					<Button
						type="button"
						variant="outline"
						className="rounded-full"
						onClick={() => onOpenChange(false)}
						disabled={submitting}
					>
						Cancel
					</Button>
					<Button
						type="button"
						className="rounded-full bg-[#32302F] px-6 text-white hover:bg-[#1a1918]"
						onClick={handlePay}
						disabled={submitting || belowMin || amountDollars <= 0}
					>
						<CreditCard />
						{submitting ? 'Redirecting…' : 'Pay'}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	)
}
